import { useEffect, useState } from "react";

import { createUser, listUsers, type UserResponse } from "@/api/users";
import { useAuth } from "@/features/auth/auth-context";

import {
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectItem,
} from "@heroui/react";

export function AdminUsersPage() {
  const { user } = useAuth();

  const [users, setUsers] = useState<UserResponse[]>([]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadUsers() {
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  if (user?.role !== "admin") {
    return (
      <div className="p-6">
        <p>Access denied.</p>
      </div>
    );
  }

  async function handleCreateUser() {
    setIsLoading(true);
    setError(null);

    try {
      await createUser({
        username,
        password,
        role,
      });

      setUsername("");
      setPassword("");
      setRole("user");

      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6 space-y-6">
      <Card>
        <CardBody className="space-y-4">
          <div>
            <h1 className="text-2xl font-semibold">User Management</h1>

            <p className="text-sm text-foreground-500">
              Create and manage users.
            </p>
          </div>

          <Input
            label="Username"
            value={username}
            onValueChange={setUsername}
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onValueChange={setPassword}
          />

          <Select
            label="Role"
            selectedKeys={[role]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0];

              if (value === "admin" || value === "user") {
                setRole(value);
              }
            }}
          >
            <SelectItem key="user">
              User
            </SelectItem>

            <SelectItem key="admin">
              Admin
            </SelectItem>
          </Select>

          {error ? (
            <p className="text-danger text-sm">{error}</p>
          ) : null}

          <Button
            color="primary"
            onPress={handleCreateUser}
            isLoading={isLoading}
          >
            Create User
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="text-xl font-semibold mb-4">
            Existing Users
          </h2>

          <div className="space-y-2">
            {users.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div>
                  <p className="font-medium">
                    {u.username}
                  </p>

                  <p className="text-sm text-foreground-500">
                    {u.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
